/*
 * KM2 Brand Tiles — shared brand → representative-product mapper.
 *
 * Phase 2 (Plan 20260627-135358). Used by the noir brand marquee (Module 2) and the
 * audience tile rails (Modules 3/4). DATA LAYER ONLY — returns plain data, never touches
 * the DOM (each section renders its own chips/tiles with XSS-safe textContent).
 *
 * Why a separate layer: the marquee/tiles need a real product PHOTO + scent label + link per
 * brand. createBrandPager wraps KM2Brands.createProductPager (one Storefront GraphQL page per
 * scroll, BEST_SELLING in-stock order) and dedupes each page into NEW-brand cards, picking a
 * representative per vendor here. This is PURE LAZY — page N is fetched only when next() is called
 * on scroll; nothing is preloaded. It replaced an earlier path (Plan 20260628-215142) that fetched
 * the whole ranked list ONCE and sliced it in memory (a preload), and before that a
 * /collections/<slug>/products.json-per-brand storm that tripped the Storefront 429 rate limit.
 *
 * Public API (window.KM2BrandTiles):
 *   createBrandPager({ key, query, audience }) -> { next(limit), reset() }
 *     - next(limit): resolves up to `limit` RENDERABLE new-brand entries, fetching one backend
 *       page per pull; resolves [] once the source is exhausted. Shape:
 *         { vendor, product: { title, image, images[], handle, url } }
 *         - image = primary photo (marquee chip); images = up to 4 (tile hover-cycle)
 *     - query: extra Storefront filter ANDed onto the unconditional available_for_sale:true +
 *       fragrance base (e.g. an audience clause). audience: 'all' | 'men' | 'women' controls the
 *       chip/tile link path (/collections/<audience>?v=<Vendor>) AND the rep audience match.
 *     - Brands with no renderable representative are skipped (graceful empty — never a broken chip).
 *
 * Depends on window.KM2Brands (assets/mega-menu-brands.js, loaded globally in header.liquid).
 */
(function () {
  if (window.KM2BrandTiles) return; // idempotent

  // slugify — MUST stay byte-for-byte identical to mega-menu-brands.js slugify() (and the
  // create-brand-collections.ts generator that produced the real /collections/<slug> handles).
  // Diverge by one char and the products.json fetch 404s.
  function slugify(name) {
    return String(name)
      .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents: Hermès -> hermes
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // The store's OWN house labels (EBC / km2) are not designer houses to shop in the brand rails —
  // they have their own dedicated EBC Spotlight section. Skip them client-side here (the rails no
  // longer carry a ranking deny-list). Pattern matches the homepage test's guard: /\bebc\b|km2/.
  function isHouseLabel(vendor) {
    var v = String(vendor || '').toLowerCase();
    return /\bebc\b/.test(v) || v.indexOf('km2') !== -1;
  }

  function audiencePath(audience) {
    var a = String(audience || 'all').toLowerCase();
    return (a === 'men' || a === 'women') ? a : 'all';
  }

  // Link a chip/tile to the brand, scoped to the audience via the kmf ?v= vendor param.
  function brandUrl(vendor, audience) {
    return '/collections/' + audiencePath(audience) + '?v=' + encodeURIComponent(vendor);
  }

  // A representative product should be in-stock AND have a real (non-placeholder) photo.
  // The store uses an "image to follow" placeholder for un-photographed products; skip those.
  function hasRealImage(p) {
    var imgs = p.images || [];
    if (!imgs.length) return false;
    return !/image[-_]?to[-_]?follow|placeholder|coming[-_]?soon/i.test(imgs[0].src || '');
  }
  // AC31: prefer the PRODUCT-LEVEL field before scanning variants. The product-level flag means
  // "any variant sells"; a variant scan only sees the FETCHED window, so an availability answer
  // would otherwise depend on how many variants the query happened to return. Where the field is
  // absent, fall back to the variant scan UNCHANGED — no delta on that path.
  function isAvailable(p) {
    if (p.availableForSale != null) return !!p.availableForSale;
    return (p.variants || []).some(function (v) { return v.available; });
  }
  function imageCount(p) {
    return (p.images || []).length;
  }
  // Fragrance = a concentration product_type OR any `type-*` tag. UNION on purpose: many real
  // fragrances (Tom Ford, Ferragamo, Orientica EDPs) carry the product_type but NO type-* tag,
  // while footwear / watches / gift-sets carry NEITHER. Single bottles only — fragrance gift
  // sets (product_type "Gift Set", no type-* tag) are intentionally excluded.
  // SHOWN product types: fragrance concentrations (incl. Eau Fraiche) + fragrance GIFT SETS
  // (Men's / Women's / Children's / Gift Set). UNION with type-* tags because many real
  // fragrances carry the product_type but no tag; footwear / watches / bath-body (shower gel,
  // lotion, deodorant) / makeup carry NEITHER and are excluded.
  var FRAGRANCE_TYPES = ['eau de parfum', 'eau de toilette', 'eau de cologne', 'parfum', 'body spray', 'aftershave', 'eau fraiche'];
  // Storefront-search form (apostrophes escaped for the JS string). product_type is the complete
  // ranking signal; the client isFragrance() additionally matches any "* gift set" + type-* tag.
  var FRAGRANCE_QUERY = '(product_type:"Eau de Parfum" OR product_type:"Eau de Toilette" OR product_type:"Eau de Cologne" OR product_type:"Parfum" OR product_type:"Body Spray" OR product_type:"Aftershave" OR product_type:"Eau Fraiche" OR product_type:"Gift Set" OR product_type:"Men\'s Gift Set" OR product_type:"Women\'s Gift Set" OR product_type:"Children\'s Gift Set")';
  function isFragrance(p) {
    var t = String(p.product_type || '').trim().toLowerCase();
    if (FRAGRANCE_TYPES.indexOf(t) !== -1) return true;
    if (t.indexOf('gift set') !== -1) return true; // fragrance gift sets (Men's/Women's/Children's/Gift Set)
    return (p.tags || []).some(function (tag) { return String(tag).toLowerCase().indexOf('type-') === 0; });
  }
  // Pick the best representative from already-scoped (audience + fragrance) products: IN-STOCK
  // only — NEVER fall back to an out-of-stock product. Prefer multi-real-photo products (the
  // store's un-photographed items carry a single "image to follow" placeholder). null => the
  // brand has no in-stock fragrance and is skipped by the caller.
  function pickRepresentative(products) {
    var pool = products.filter(isAvailable);
    if (!pool.length) return null;
    var photographed = pool.filter(hasRealImage);
    var rich = photographed.filter(function (p) { return imageCount(p) >= 2; });
    return rich[0] || photographed[0] || pool[0];
  }

  // "Signature scent" label — the product title with a redundant leading vendor prefix
  // stripped (house name already shows above it), so "Dior Sauvage EDP" reads "Sauvage EDP".
  function scentLabel(title, vendor) {
    var t = String(title || '').trim();
    var v = String(vendor || '').trim();
    if (v && t.toLowerCase().indexOf(v.toLowerCase()) === 0) {
      var stripped = t.slice(v.length).replace(/^[\s\-–—:|·]+/, '').trim();
      if (stripped) return stripped;
    }
    return t;
  }

  // A 600px tile rendition via the ONE resize helper, KM2ProductModel.imageUrl (D14) — no local
  // copy of the suffix rule. Model absence is a load-order bug: report it, keep the raw photo.
  function tileImg(url) {
    var M = window.KM2ProductModel;
    if (!M) {
      if (window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'km2-brand-tiles');
      return url || '';
    }
    return M.imageUrl(url, 600);
  }

  // A men/women rail must show an audience-APPROPRIATE product, not just any product
  // from the vendor's collection (e.g. a women's shoe must not represent a brand in the
  // Men rail). products.json carries `tags`; match the same audience tags the ranking uses.
  function matchesAudience(p, audience) {
    if (audience !== 'men' && audience !== 'women') return true; // 'all' — no constraint
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    return tags.indexOf(audience) !== -1 || tags.indexOf('unisex') !== -1;
  }

  // Adapt a Storefront GraphQL product node (from KM2Brands.createProductPager) to the
  // products.json-ish shape the selection helpers above expect, so pickRepresentative /
  // hasRealImage / isFragrance / matchesAudience stay the SINGLE source of selection policy
  // (DRY — no second copy of "what's a good representative"). featuredImage leads, then
  // images(first:4), deduped.
  //
  // Availability is REAL, not synthesised (plan 20260901-050844 T7). This mapper used to emit
  // `variants: [{ available: true }]` on the reasoning that the query is server-filtered to
  // available_for_sale:true — which made isAvailable() a tautology at every call site. It now
  // carries the node's own product-level `availableForSale` AND maps the fetched variants, so
  // isAvailable() answers from data for the first time. Round-4 r4-f2: the product-level field is
  // the one AC31 asks the guard to prefer, and isAvailable() is only ever called on this mapper's
  // output — so without it here, AC31 is unsatisfiable at this call site.
  //
  // This stays a SELECTION-POLICY mapper in the products.json-ish shape, deliberately NOT migrated
  // to KM2ProductModel (T5 round-1 f2 keeps that seam): its `variants` array is read only by
  // isAvailable(), while cardFor sources the card-contract variants from the adapter separately.
  function nodeToProduct(node) {
    var urls = [];
    if (node.featuredImage && node.featuredImage.url) urls.push(node.featuredImage.url);
    var edges = (node.images && node.images.edges) || [];
    for (var i = 0; i < edges.length; i++) {
      var u = edges[i] && edges[i].node && edges[i].node.url;
      if (u && urls.indexOf(u) === -1) urls.push(u);
    }
    var vedges = (node.variants && node.variants.edges) || [];
    return {
      title: node.title,
      handle: node.handle,
      product_type: node.productType,
      tags: node.tags || [],
      images: urls.map(function (u) { return { src: u }; }),
      availableForSale: node.availableForSale,
      variants: vedges.map(function (e) {
        var vn = (e && e.node) || {};
        return { available: !!vn.availableForSale };
      }),
    };
  }

  // Maison-aware emission scheduler for the HOUSES rail. Sits on top of the pager's lazy
  // first-seen (BEST_SELLING) queue and re-sequences vendors so that (owner rule, 2026-07-03):
  //   1. the rail LEADS with a Brand Spotlight (maison) whenever one exists, and
  //   2. no two spotlights are emitted within `gap` non-maison tiles of each other
  //      (minimum `gap` regular houses between spotlights — they're never side-by-side).
  // Pure + stateful, framework-free: feed(vendor) in source order, take(mode) pulls the next
  // vendor to render, onEmitted(vendor) records what ACTUALLY rendered (null-rep vendors are
  // dropped by the pager and never call onEmitted, so they don't consume the gap or the lead).
  // mode: 'normal' (park regulars while hunting the lead maison, else signal "fetch more" via ''),
  //       'pageLimit' (this next() spent its page budget — stop hunting, flush parked regulars),
  //       'exhausted' (source drained — flush everything, gap may be unmet only at the very tail).
  // Validated against the 5-scenario harness in scratchpad/maison-sched.test.js (lead / spacing /
  // no-stranding / no-stall / null-rep). An empty maison list makes it a pass-through (budget 0).
  function createMaisonScheduler(isMaison, gap, maisonBudget) {
    var queue = [];         // fed vendors, source order, not yet classified/emitted
    var heldMaisons = [];   // maisons deferred until the spacing gap is met
    var heldLead = [];      // regulars parked while hunting for the FIRST (lead) maison
    var sinceMaison = gap;  // start "cold" so a lead maison is allowed immediately
    var leadDone = false;
    var maisonsLeft = maisonBudget; // distinct maisons still expected — bounds the lead hunt

    function maisonAllowed() { return !leadDone || sinceMaison >= gap; }
    function flushLead() { if (heldLead.length) { queue = heldLead.concat(queue); heldLead = []; } }

    return {
      feed: function (v) { queue.push(v); },
      take: function (mode) {
        var canParkLead = (mode === 'normal');
        if (heldMaisons.length && maisonAllowed()) { maisonsLeft--; return heldMaisons.shift(); }
        while (queue.length) {
          var v = queue.shift();
          if (isMaison(v)) {
            if (maisonAllowed()) { maisonsLeft--; return v; }
            heldMaisons.push(v); continue;               // too close to the last spotlight
          }
          if (!leadDone && canParkLead && !heldMaisons.length && maisonsLeft > 0) {
            heldLead.push(v); continue;                  // park it — hunt for a maison to lead
          }
          // lead hunt is over for this vendor — emit any parked regulars first, in source order
          if (heldLead.length) { queue.unshift(v); flushLead(); continue; }
          return v;
        }
        if (mode === 'normal') return '';                // more pages may bring the lead maison
        if (heldLead.length) return heldLead.shift();    // give up the hunt — flush parked regulars
        if (mode === 'exhausted' && heldMaisons.length) { maisonsLeft--; return heldMaisons.shift(); }
        return '';
      },
      onEmitted: function (v) {
        if (isMaison(v)) { sinceMaison = 0; if (!leadDone) flushLead(); leadDone = true; }
        else { sinceMaison++; leadDone = true; }
      }
    };
  }

  // Pure-lazy brand pager: wraps a product page source (KM2Brands.createProductPager — BEST_SELLING
  // in-stock fragrances, audience-filtered) and dedupes each fetched page into NEW-brand cards.
  // next(limit) pulls pages until it has `limit` brands not seen before, or the source is exhausted.
  // No global ranking, no preload — page N is fetched only when next() is called (i.e. on scroll).
  // This REPLACED the old createPager/getBrands path that fetched the whole ranked list ONCE and
  // sliced it in memory (a preload the user rejected). rep selection still lives in the shared
  // granular helpers (nodeToProduct/pickRepresentative/scentLabel/…) — one source of policy (DRY).
  //   createBrandPager({ key, query, audience }) -> { next(limit), reset() }
  //     - next(limit): resolves up to `limit` RENDERABLE new-brand { vendor, product } entries,
  //       fetching one backend page per pull; resolves [] once the source is exhausted.
  function createBrandPager(opts) {
    opts = opts || {};
    var audience = opts.audience || 'all';
    // Optional ceiling on TOTAL houses emitted across all next() calls. 0/absent = unlimited
    // (the historical behaviour every existing caller relies on). The cap belongs here, not in
    // a caller: this is the layer that knows how many distinct houses have been emitted, and
    // putting it here means the rail's existing exhaustion path drives the loop for free —
    // a capped pager simply reports a short final page, exactly like a genuinely exhausted one.
    var cap = Math.max(0, Number(opts.cap) || 0);
    var emitted = 0;
    // In-stock ONLY, ALWAYS — available_for_sale:true is unconditional so the backend never returns
    // (and we never lazy-load) an out-of-stock product. Fragrance filter + any audience filter ANDed on.
    var query = 'available_for_sale:true AND ' + FRAGRANCE_QUERY + (opts.query ? ' AND (' + opts.query + ')' : '');
    var KB = window.KM2Deps
      ? window.KM2Deps.need('KM2Brands', 'createProductPager', 'km2-brand-tiles')
      : ((window.KM2Brands && typeof window.KM2Brands.createProductPager === 'function') ? window.KM2Brands : null);
    var src = KB ? KB.createProductPager({ query: query, sortKey: 'BEST_SELLING' }) : null;
    var seen = Object.create(null);     // vendor -> true (already EMITTED, deduped across all pages)
    var pending = Object.create(null);  // vendor -> [productNode] accumulated for each vendor
    var exhausted = false;
    var MAX_PAGES_PER_CALL = 6;         // bound the work per next() for sparse pages

    // Brand Spotlight (maison) sequencing — the houses rail leads with a maison and keeps
    // spotlights >= MAISON_GAP regular houses apart (owner rule 2026-07-03). Maison membership is
    // the SAME window.KM2Maisons list the section reads for the large-tile styling (one source of
    // truth — the section still owns the VISUAL; this owns only emission ORDER). audience is
    // 'men' | 'women' | 'all'; 'all' or an empty list => the scheduler is a pass-through.
    var maisonList = (window.KM2Maisons && window.KM2Maisons[audience]) || [];
    function isMaisonVendor(v) { return maisonList.indexOf(String(v || '').toLowerCase()) !== -1; }
    var MAISON_GAP = 4;                 // >= 4 non-maison houses between two spotlights
    var scheduler = createMaisonScheduler(isMaisonVendor, MAISON_GAP, maisonList.length);

    function emit(vendor) {
      var nodes = pending[vendor] || [];
      var scoped = nodes.map(nodeToProduct).filter(function (p) { return matchesAudience(p, audience) && isFragrance(p); });
      var p = pickRepresentative(scoped);
      if (!p) return null;
      var sized = (p.images || []).map(function (i) { return i.src; }).slice(0, 4).map(tileImg);
      return { vendor: vendor, product: { title: scentLabel(p.title, vendor), image: sized[0] || '', images: sized, handle: p.handle || '', url: brandUrl(vendor, audience) } };
    }

    // Emit vendors via the maison scheduler until `out` reaches `limit` or the scheduler needs more
    // input. The scheduler re-sequences the first-seen (BEST_SELLING) queue for the lead + spacing
    // rule; draining before fetching another page still prevents stranding mid-rank brands a single
    // page surfaced past the first `limit`. `mode` tells the scheduler how hard to hold maisons /
    // lead-hunt regulars: 'normal' during fetch, 'pageLimit'/'exhausted' at a give-up point (flush).
    // Only ACTUALLY-rendered tiles call onEmitted, so a null-rep vendor never consumes the gap/lead.
    function drain(out, limit, mode) {
      while (out.length < limit) {
        if (cap && emitted >= cap) { exhausted = true; break; } // ceiling reached → behave as exhausted
        var vendor = scheduler.take(mode);
        if (!vendor) break;              // '' => needs more pages (or nothing left)
        if (seen[vendor]) continue;      // safety — each vendor is fed once
        seen[vendor] = true;             // mark emitted even if null-rep, so we don't re-queue it
        var entry = emit(vendor);
        if (entry) { out.push(entry); emitted++; scheduler.onEmitted(vendor); }
      }
    }

    function next(limit) {
      limit = Math.max(1, Number(limit) || 1);
      if (!src) return Promise.resolve([]);
      var out = [];
      var pages = 0;
      function pull() {
        drain(out, limit, 'normal');     // emit what we already hold before fetching more
        if (out.length >= limit) return out;
        if (cap && emitted >= cap) return out; // ceiling reached — never fetch another page
        // Give-up points: flush the scheduler's parked maisons / lead-hunt regulars so a batch is
        // never empty while the source still has (or had) houses — the rail must always progress.
        if (exhausted) { drain(out, limit, 'exhausted'); return out; }
        if (pages >= MAX_PAGES_PER_CALL) { drain(out, limit, 'pageLimit'); return out; }
        pages++;
        return src.nextPage().then(function (page) {
          if (!page.hasMore) exhausted = true;
          (page.nodes || []).forEach(function (n) {
            if (!n || !n.vendor || isHouseLabel(n.vendor)) return; // skip the store's own house (EBC/km2)
            if (!pending[n.vendor]) { pending[n.vendor] = []; scheduler.feed(n.vendor); } // first sight → queue
            pending[n.vendor].push(n);
          });
          return pull();
        });
      }
      return Promise.resolve(pull());
    }

    return { next: next, reset: function () { /* stateful source; no reset for pure-lazy */ } };
  }

  // Pure-lazy PER-PRODUCT pager: like createBrandPager but it does NOT dedupe to one card per house —
  // every in-stock, audience-matching, photographed fragrance becomes its OWN card (one card per
  // scent, e.g. Dior Sauvage AND Dior Homme both show). Cards link to the product page (/products/…)
  // like the EBC carousel. Used by the For Him / For Her rails so their counter reflects PRODUCTS,
  // not houses. Reuses the same selection helpers (one source of policy). A leftover buffer carries
  // un-emitted products across next() calls so none are stranded when a 50-product page overshoots.
  //   createScentPager({ key, query, audience }) -> { next(limit), reset() }
  function createScentPager(opts) {
    opts = opts || {};
    var audience = opts.audience || 'all';
    var query = 'available_for_sale:true AND ' + FRAGRANCE_QUERY + (opts.query ? ' AND (' + opts.query + ')' : '');
    var KB = window.KM2Deps
      ? window.KM2Deps.need('KM2Brands', 'createProductPager', 'km2-brand-tiles')
      : ((window.KM2Brands && typeof window.KM2Brands.createProductPager === 'function') ? window.KM2Brands : null);
    var src = KB ? KB.createProductPager({ query: query, sortKey: 'BEST_SELLING' }) : null;
    var buffer = [];          // cards fetched but not yet emitted (drained in order — no stranding)
    var exhausted = false;
    var MAX_PAGES_PER_CALL = 6;

    // One product node -> one card, or null if it isn't a renderable in-stock audience fragrance.
    function cardFor(node) {
      if (!node || !node.vendor || isHouseLabel(node.vendor)) return null; // EBC has its own section
      var p = nodeToProduct(node);
      if (!matchesAudience(p, audience) || !isFragrance(p) || !isAvailable(p) || !hasRealImage(p)) return null;
      var sized = (p.images || []).map(function (i) { return i.src; }).slice(0, 4).map(tileImg);
      // Tile price for the product card (EBC-style) — KM2ProductModel.displayPrice (finding 513):
      // cheapest in-stock size, `from` when dearer sizes exist. The brand pager omits variants, so
      // its tiles get a NULL price === a house card; the section renders price only when present.
      // Variants ride along for the gold quick-add on product tiles (owner 2026-07-02):
      // single-variant adds direct via /cart/add.js, a second fetched variant flags
      // multi-variant -> the card routes to the PDP instead.
      //
      // PARTIAL migration by design (plan 20260901-050844 T5, round-1 f2). cardFor does NOT
      // return a card product — it returns a TILE model, and two sections depend on that exact
      // shape: sections/audience-tiles.liquid discriminates HOUSE tiles from product tiles on a
      // NULL product price, and sections/brand-marquee.liquid reads url/image/title. Handing
      // them raw adapter output would make the price `undefined` rather than `null`, flipping
      // every product tile into the brand-tile branch and blanking the tile photo. So the tile
      // projection below stays exactly as it was, and the adapter is used ONLY for the variants
      // array — which is where the new variant `title` a size picker needs comes from.
      var MODEL = window.KM2ProductModel;
      var variants, dp = null;
      if (MODEL) {
        var cp = MODEL.productFromStorefrontNode(node);
        variants = cp.variants;
        dp = MODEL.displayPrice(cp);
      } else {
        if (window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'km2-brand-tiles');
        variants = [];
      }
      var price = dp ? dp.price : null;                 // null when no priced size — the house-tile branch
      var compareAt = dp ? dp.compare_at_price : null;
      return { vendor: node.vendor, product: { title: scentLabel(p.title, node.vendor), product_type: p.product_type, image: sized[0] || '', images: sized, handle: p.handle || '', url: '/products/' + (p.handle || ''), price: price, compareAt: compareAt, from: !!(dp && dp.from), variants: variants } };
    }

    function next(limit) {
      limit = Math.max(1, Number(limit) || 1);
      if (!src) return Promise.resolve([]);
      var out = [];
      var pages = 0;
      function pull() {
        while (out.length < limit && buffer.length) out.push(buffer.shift()); // drain leftovers first
        if (out.length >= limit || exhausted || pages >= MAX_PAGES_PER_CALL) return out;
        pages++;
        return src.nextPage().then(function (page) {
          if (!page.hasMore) exhausted = true;
          (page.nodes || []).forEach(function (n) { var c = cardFor(n); if (c) buffer.push(c); });
          while (out.length < limit && buffer.length) out.push(buffer.shift());
          return pull();
        });
      }
      return Promise.resolve(pull());
    }

    return { next: next, reset: function () { /* stateful source; no reset for pure-lazy */ } };
  }

  // Storefront query is a quoted DSL: escape backslash + double-quote so a vendor value can't
  // break (or inject into) the query. Vendor data is trusted (Shopify) — cheap A03 defense-in-depth.
  function escQ(s) { return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"'); }

  // Audience filter mirrors audience-tiles queryFor(): men/women add their tag OR unisex.
  function audienceFilter(aud) {
    if (aud === 'men') return ' AND (tag:men OR tag:unisex)';
    if (aud === 'women') return ' AND (tag:women OR tag:unisex)';
    return '';
  }

  // Per-vendor, in-stock-ONLY product pager for the tile hover-cycle. Wraps the SAME
  // KM2Brands.createProductPager (one ~50-product Storefront page per nextPage(), cursor in
  // closure) the rails use — TRUE lazy, nothing preloaded. next(limit) drains buffered products
  // (ONE image each — featuredImage / first real photo, deduped by handle), fetching another
  // backend page only when the buffer runs dry, until `limit` renderable frames are produced or
  // the source exhausts. Mirrors the createBrandPager drain pattern (no stranding, no preload).
  //   createBrandScentPager(vendor, audience) -> { next(limit) -> Promise<[{image,title,handle}]> }
  function createBrandScentPager(vendor, audience) {
    var query = 'available_for_sale:true AND vendor:"' + escQ(vendor) + '"' + audienceFilter(audience);
    var KBv = window.KM2Deps
      ? window.KM2Deps.need('KM2Brands', 'createProductPager', 'km2-brand-tiles/scentPager')
      : ((window.KM2Brands && window.KM2Brands.createProductPager) ? window.KM2Brands : null);
    var src = KBv ? KBv.createProductPager({ query: query, sortKey: 'BEST_SELLING' }) : null;
    var buffer = [];
    var exhausted = !src;
    var seen = Object.create(null); // dedupe by handle across pages
    function toFrame(node) {
      if (!node) return null;
      var url = (node.featuredImage && node.featuredImage.url) || '';
      if (!url) {
        var edges = (node.images && node.images.edges) || [];
        if (edges.length && edges[0].node) url = edges[0].node.url;
      }
      if (!url || /no-image|placeholder/i.test(url)) return null; // real photo only
      var handle = node.handle || '';
      if (handle && seen[handle]) return null;
      if (handle) seen[handle] = true;
      return { image: tileImg(url), title: scentLabel(node.title, vendor), handle: handle };
    }
    function next(limit) {
      limit = Math.max(1, Number(limit) || 1);
      var out = [];
      function pull() {
        while (buffer.length && out.length < limit) {
          var f = toFrame(buffer.shift());
          if (f) out.push(f);
        }
        if (out.length >= limit || exhausted) return out;
        return src.nextPage().then(function (page) {
          var nodes = (page && page.nodes) || [];
          if (!nodes.length || !page.hasMore) exhausted = true;
          for (var i = 0; i < nodes.length; i++) buffer.push(nodes[i]);
          if (!nodes.length) return out;
          return pull();
        }).catch(function () { exhausted = true; return out; });
      }
      return Promise.resolve(pull());
    }
    return { next: next };
  }

  window.KM2BrandTiles = {
    createBrandPager: createBrandPager, // pure-lazy pager, one card per HOUSE (Featured Houses marquee)
    createScentPager: createScentPager, // pure-lazy pager, one card per PRODUCT (For Him / For Her rails)
    createBrandScentPager: createBrandScentPager, // per-vendor lazy pager for the tile hover-cycle
    slugify: slugify,     // exposed for tests / parity checks
    isFragrance: isFragrance, // reused by the EBC carousel's lazy-load JS
    brandUrl: brandUrl,
  };
})();
